import React, { useEffect, useState } from 'react';
import {
  calendarService,
  CalendarReviewer,
  TabletopAppointment,
  TestConnectionResponse,
} from '../../services/calendarService';

interface ManageReviewersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ManageReviewersModal: React.FC<ManageReviewersModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<'reviewers' | 'appointments'>('reviewers');
  const [reviewers, setReviewers] = useState<CalendarReviewer[]>([]);
  const [appointments, setAppointments] = useState<TabletopAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Test connection state
  const [testingId, setTestingId] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<TestConnectionResponse | null>(null);

  // Edit / Add state
  const [editingReviewer, setEditingReviewer] = useState<CalendarReviewer | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    title: '',
    timezone: 'America/Toronto',
    slot_duration_minutes: 30,
    buffer_minutes: 15,
    min_notice_hours: 24,
    max_days_ahead: 30,
    is_active: true,
    is_default: false,
    google_calendar_id: '',
  });
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [revData, apptData] = await Promise.all([
        calendarService.listReviewers(),
        calendarService.listAllAppointments(),
      ]);
      setReviewers(revData.reviewers);
      setAppointments(apptData.appointments);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load reviewers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleTestConnection = async (id: number) => {
    try {
      setTestingId(id);
      setTestResult(null);
      setError(null);
      const res = await calendarService.testConnection(id);
      setTestResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Test connection failed');
    } finally {
      setTestingId(null);
    }
  };

  const openAddForm = () => {
    setForm({
      name: '',
      email: '',
      title: 'Lead Resilience Assessor & ISO 22301 Auditor',
      timezone: 'America/Toronto',
      slot_duration_minutes: 30,
      buffer_minutes: 15,
      min_notice_hours: 24,
      max_days_ahead: 30,
      is_active: true,
      is_default: reviewers.length === 0,
      google_calendar_id: '',
    });
    setEditingReviewer(null);
    setIsAdding(true);
  };

  const openEditForm = (rev: CalendarReviewer) => {
    setForm({
      name: rev.name,
      email: rev.email,
      title: rev.title || '',
      timezone: rev.timezone || 'America/Toronto',
      slot_duration_minutes: rev.slot_duration_minutes || 30,
      buffer_minutes: rev.buffer_minutes || 15,
      min_notice_hours: rev.min_notice_hours || 24,
      max_days_ahead: rev.max_days_ahead || 30,
      is_active: rev.is_active,
      is_default: rev.is_default,
      google_calendar_id: rev.google_calendar_id || '',
    });
    setEditingReviewer(rev);
    setIsAdding(false);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError('Name and Google Email are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      if (editingReviewer) {
        await calendarService.updateReviewer(editingReviewer.id, {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          title: form.title.trim() || undefined,
          timezone: form.timezone.trim(),
          slot_duration_minutes: Number(form.slot_duration_minutes),
          buffer_minutes: Number(form.buffer_minutes),
          min_notice_hours: Number(form.min_notice_hours),
          max_days_ahead: Number(form.max_days_ahead),
          is_active: form.is_active,
          is_default: form.is_default,
          google_calendar_id: form.google_calendar_id.trim() || undefined,
        });
        setSuccess('Reviewer updated successfully');
      } else {
        await calendarService.createReviewer({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          title: form.title.trim() || undefined,
          timezone: form.timezone.trim(),
          slot_duration_minutes: Number(form.slot_duration_minutes),
          buffer_minutes: Number(form.buffer_minutes),
          min_notice_hours: Number(form.min_notice_hours),
          max_days_ahead: Number(form.max_days_ahead),
          is_active: form.is_active,
          is_default: form.is_default,
          google_calendar_id: form.google_calendar_id.trim() || undefined,
        });
        setSuccess('Reviewer created successfully');
      }

      setIsAdding(false);
      setEditingReviewer(null);
      await loadData();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save reviewer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this reviewer?')) return;
    try {
      setError(null);
      await calendarService.deleteReviewer(id);
      setSuccess('Reviewer removed');
      await loadData();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Cannot delete reviewer');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-blue-100 text-blue-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </span>
              <h2 className="text-lg font-bold text-[#0c1e40]">Reviewers & Google Calendar</h2>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Configure Rob&apos;s Google Calendar availability and manage tabletop exercise review appointments.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-200/60 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Sub-tabs */}
        <div className="px-6 border-b border-slate-200 flex gap-6 text-xs font-semibold">
          <button
            type="button"
            onClick={() => {
              setActiveTab('reviewers');
              setIsAdding(false);
              setEditingReviewer(null);
            }}
            className={`py-3 border-b-2 transition-colors ${
              activeTab === 'reviewers'
                ? 'border-[#991b1b] text-[#0c1e40]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            Reviewers & Calendars ({reviewers.length})
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('appointments');
              setIsAdding(false);
              setEditingReviewer(null);
            }}
            className={`py-3 border-b-2 transition-colors ${
              activeTab === 'appointments'
                ? 'border-[#991b1b] text-[#0c1e40]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            Booked Appointments ({appointments.length})
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {loading ? (
            <div className="py-12 text-center text-xs text-slate-500 space-y-2">
              <div className="w-6 h-6 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
              <div>Loading reviewers & calendar settings...</div>
            </div>
          ) : (
            <>
              {error && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800">
                  {error}
                </div>
              )}

              {success && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800">
                  {success}
                </div>
              )}

              {testResult && (
                <div className={`p-3.5 rounded-xl text-xs border ${
                  testResult.success
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : 'bg-amber-50 border-amber-200 text-amber-800'
                }`}>
                  <div className="font-bold flex items-center gap-1.5">
                    {testResult.success ? '✓ Google Calendar Connection Verified' : '⚠ Connection Notice'}
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-white/70 border border-current">
                      Mode: {testResult.mode}
                    </span>
                  </div>
                  <p className="mt-1">{testResult.message}</p>
                  {testResult.mode === 'dev_fallback' && (
                    <p className="mt-1 text-[11px] opacity-80">
                      Tip: In production, supply <code>GOOGLE_SERVICE_ACCOUNT_JSON</code> or share the calendar with Flash AI’s service account to check live Google Calendar Free/Busy times.
                    </p>
                  )}
                </div>
              )}

          {/* TAB 1: REVIEWERS LIST & FORM */}
          {activeTab === 'reviewers' && (
            <>
              {isAdding || editingReviewer ? (
                /* ADD / EDIT FORM */
                <form onSubmit={handleSave} className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
                  <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                    <h3 className="text-sm font-bold text-[#0c1e40]">
                      {editingReviewer ? `Edit ${editingReviewer.name}` : 'Add New Reviewer'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAdding(false);
                        setEditingReviewer(null);
                      }}
                      className="text-xs text-slate-500 hover:text-slate-800 font-semibold"
                    >
                      Cancel
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">Reviewer Name *</label>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="e.g. Rob"
                        className="w-full border border-slate-300 rounded-lg p-2 bg-white"
                      />
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">Google Calendar Email *</label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="rob@company.com"
                        className="w-full border border-slate-300 rounded-lg p-2 bg-white"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block font-semibold text-slate-700 mb-1">Professional Title</label>
                      <input
                        type="text"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        placeholder="Lead Resilience Assessor & ISO 22301 Auditor"
                        className="w-full border border-slate-300 rounded-lg p-2 bg-white"
                      />
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">Timezone</label>
                      <select
                        value={form.timezone}
                        onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                        className="w-full border border-slate-300 rounded-lg p-2 bg-white"
                      >
                        <option value="America/Toronto">America/Toronto (Eastern)</option>
                        <option value="America/New_York">America/New_York (Eastern)</option>
                        <option value="America/Chicago">America/Chicago (Central)</option>
                        <option value="America/Denver">America/Denver (Mountain)</option>
                        <option value="America/Los_Angeles">America/Los_Angeles (Pacific)</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">Slot Duration</label>
                      <select
                        value={form.slot_duration_minutes}
                        onChange={(e) => setForm({ ...form, slot_duration_minutes: Number(e.target.value) })}
                        className="w-full border border-slate-300 rounded-lg p-2 bg-white"
                      >
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes (Standard)</option>
                        <option value={45}>45 minutes</option>
                        <option value={60}>60 minutes</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">Buffer Between Calls</label>
                      <select
                        value={form.buffer_minutes}
                        onChange={(e) => setForm({ ...form, buffer_minutes: Number(e.target.value) })}
                        className="w-full border border-slate-300 rounded-lg p-2 bg-white"
                      >
                        <option value={0}>0 minutes</option>
                        <option value={10}>10 minutes</option>
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">Minimum Advance Notice</label>
                      <select
                        value={form.min_notice_hours}
                        onChange={(e) => setForm({ ...form, min_notice_hours: Number(e.target.value) })}
                        className="w-full border border-slate-300 rounded-lg p-2 bg-white"
                      >
                        <option value={0}>Same day allowed</option>
                        <option value={12}>12 hours in advance</option>
                        <option value={24}>24 hours (1 day)</option>
                        <option value={48}>48 hours (2 days)</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 pt-2">
                    <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.is_active}
                        onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      Active for Booking
                    </label>

                    <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.is_default}
                        onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      Primary / Default Reviewer
                    </label>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200">
                    <button
                      type="button"
                      onClick={() => {
                        setIsAdding(false);
                        setEditingReviewer(null);
                      }}
                      className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200/60 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-4 py-1.5 text-xs font-bold bg-[#0c1e40] hover:bg-slate-800 text-white rounded-lg disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : editingReviewer ? 'Update Reviewer' : 'Create Reviewer'}
                    </button>
                  </div>
                </form>
              ) : (
                /* REVIEWERS CARDS */
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">
                      Manage Google Calendar accounts synced with Flash AI.
                    </span>
                    <button
                      type="button"
                      onClick={openAddForm}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-[#991b1b] text-white hover:bg-[#7f1d1d] transition-colors"
                    >
                      <span>+ Add Reviewer</span>
                    </button>
                  </div>

                  {reviewers.map((rev) => (
                    <div
                      key={rev.id}
                      className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs hover:border-slate-300 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-bold text-[#0c1e40]">{rev.name}</h4>
                          {rev.is_default && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800">
                              Primary Reviewer
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            rev.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {rev.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>

                        <p className="text-xs text-slate-600">
                          {rev.title || 'Resilience Assessor'} &bull; <span className="font-mono">{rev.email}</span>
                        </p>

                        <div className="text-[11px] text-slate-400 flex items-center gap-3">
                          <span>Timezone: {rev.timezone}</span>
                          <span>&bull;</span>
                          <span>Slot: {rev.slot_duration_minutes}m (Buffer: {rev.buffer_minutes}m)</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleTestConnection(rev.id)}
                          disabled={testingId === rev.id}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-700 transition-colors disabled:opacity-50"
                        >
                          {testingId === rev.id ? 'Testing...' : 'Test Connection'}
                        </button>

                        <button
                          type="button"
                          onClick={() => openEditForm(rev)}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          Edit
                        </button>

                        {reviewers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleDelete(rev.id)}
                            className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                            title="Delete Reviewer"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* TAB 2: SCHEDULED APPOINTMENTS */}
          {activeTab === 'appointments' && (
            <div className="space-y-3">
              {appointments.length === 0 ? (
                <div className="p-8 text-center bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-500">
                  No review appointments have been booked yet.
                </div>
              ) : (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold">
                      <tr>
                        <th className="px-4 py-3">Client</th>
                        <th className="px-4 py-3">Exercise</th>
                        <th className="px-4 py-3">Reviewer</th>
                        <th className="px-4 py-3">Scheduled Time</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3 text-right">Meeting</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {appointments.map((appt) => (
                        <tr key={appt.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-semibold text-slate-900">
                            {appt.client_name || appt.client_email}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            Exercise #{appt.exercise_id}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {appt.reviewer_name}
                          </td>
                          <td className="px-4 py-3 text-slate-800">
                            {new Date(appt.start_time).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              appt.status === 'scheduled'
                                ? 'bg-emerald-100 text-emerald-800'
                                : appt.status === 'rescheduled'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {appt.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {appt.google_meet_link ? (
                              <a
                                href={appt.google_meet_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:underline"
                              >
                                Google Meet &rarr;
                              </a>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageReviewersModal;
